import { useBreakpointValue, useDisclosure, useUpdateEffect } from '@chakra-ui/react'
import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'

export function openFullscreen() {
  document.documentElement.requestFullscreen()
}

export function closeFullscreen() {
  document.exitFullscreen()
}

export function useBreakpoint() {
  const current = useBreakpointValue(
    { base: 0, sm: 1, md: 2, lg: 3, xl: 4, '2xl': 5 },
    { ssr: false }
  )
  const previous = useRef<number>(0)
  useEffect(() => {
    if (current !== undefined) {
      previous.current = current
    }
  }, [current])

  return current !== undefined ? current : previous.current
}

export function useBeforeUnload(enabled: boolean, onAbort?: () => void, cleanup?: () => void) {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    function showConfirm(): boolean {
      const confirmed = window.confirm("Save isn't complete. Are you sure you want to leave?")
      if (confirmed && cleanup) {
        cleanup()
      } else if (onAbort) {
        onAbort()
      }
      return confirmed
    }

    function handleBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault()
      e.returnValue = "Save isn't complete. Are you sure you want to leave?"
      if (cleanup) {
        cleanup()
      }
    }

    function handlePopState() {
      if (enabled && !showConfirm()) {
        navigate(location.pathname, { replace: true })
      }
    }

    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState

    function patchHistoryMethod(method: typeof window.history.pushState) {
      return function (this: History, ...args: Parameters<typeof method>) {
        if (enabled && !showConfirm()) {
          return
        }
        return method.apply(this, args)
      }
    }

    if (enabled) {
      window.addEventListener('beforeunload', handleBeforeUnload)
      window.addEventListener('popstate', handlePopState)
      window.history.pushState = patchHistoryMethod(originalPushState)
      window.history.replaceState = patchHistoryMethod(originalReplaceState)
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
    }
  }, [enabled, location.pathname, navigate, cleanup])
}

export { useDisclosure, useUpdateEffect }
